import { z } from 'zod';
import { Effect, SequenceSettings, TonEffekt } from './timeline.js';
/**
 * Vorlagen (S24). Sascha: „Einmal einstellen, immer wieder benutzen.“
 *
 * Zwei Sorten -- und seit dem 3. November eine dritte (KI-Schnitt, 7.66):
 *
 *  * **Filter** -- was an einem Clip eingestellt ist: Look, Farbkorrektur,
 *    Farbräder, Weichzeichnen, Vignette, dazu die Werkzeuge für den Ton. Wer
 *    zehn Aufnahmen desselben Drehs gleich aussehen lassen will, stellt es
 *    einmal ein.
 *  * **Export** -- Bildformat, Bildrate und Ziellautheit. „Reel hochkant“ ist
 *    eine Antwort auf drei Fragen, und sie ist jedes Mal dieselbe.
 *
 *  * **KI** -- eine Anweisung an das Modell, das den Rohschnitt macht
 *    (Sascha: „Glaube wir brauchen da Prompt-Vorlagen“). „Moderation
 *    säubern“ und „Interview zu Beitrag“ sind verschiedene Aufträge an
 *    dasselbe Material, und jeder will einmal gut formuliert sein.
 *
 * Was eine Vorlage **nicht** enthält, ist so wichtig wie das, was drin ist.
 * Keine Zeiten, keine Stellen, keine Quelle: eine Vorlage, die auch die Länge
 * mitbrächte, wäre beim zweiten Clip im Weg. Und keine Blenden -- die gehören
 * zum Schnitt und nicht zum Aussehen.
 */
export const FilterVorlage = z.object({
    art: z.literal('filter'),
    effects: z.array(Effect).max(8).default([]),
    tonEffekte: z.array(TonEffekt).max(3).default([]),
});
export const ExportVorlage = z.object({
    art: z.literal('export'),
    width: SequenceSettings.shape.width,
    height: SequenceSettings.shape.height,
    fps: SequenceSettings.shape.fps,
    zielLautheit: SequenceSettings.shape.zielLautheit,
});
/**
 * Eine Anweisung für den KI-Schnitt (7.66).
 *
 * `modus` ist die eine Entscheidung, die das Modell nicht treffen darf:
 * **herausnehmen** heisst, die Reihenfolge bleibt und es fällt nur etwas weg
 * (Versprecher, Wiederholungen) -- das kann auch ein kleines Modell.
 * **auswählen** heisst, das Modell bringt Abschnitte in eine neue
 * Reihenfolge -- das braucht Überblick und ein grosses Modell.
 */
export const KiVorlage = z.object({
    art: z.literal('ki'),
    modus: z.enum(['herausnehmen', 'auswaehlen']),
    anweisung: z.string().trim().min(1).max(4000),
    /** Wie lang das Ergebnis ungefähr werden soll, oder `null` für „egal“. */
    zielSekunden: z.number().min(5).max(3600).nullable().default(null),
});
export const VorlagenDaten = z.discriminatedUnion('art', [FilterVorlage, ExportVorlage, KiVorlage]);
export const VorlagenArt = z.enum(['filter', 'export', 'ki']);
/** Was von einem Clip in eine Vorlage geht. */
export function filterVorlageAus(clip) {
    return FilterVorlage.parse({
        art: 'filter',
        effects: clip.effects ?? [],
        tonEffekte: clip.tonEffekte ?? [],
    });
}
/** Was von einem Schnitt in eine Vorlage geht. */
export function exportVorlageAus(settings) {
    return ExportVorlage.parse({
        art: 'export',
        width: settings.width,
        height: settings.height,
        fps: settings.fps,
        zielLautheit: settings.zielLautheit,
    });
}
/**
 * Die Änderung an einem Clip, die diese Vorlage bedeutet.
 *
 * **Ersetzen und nicht dazulegen.** Eine Vorlage zweimal auf denselben Clip
 * anzuwenden muss dasselbe ergeben wie einmal -- sonst stapeln sich acht
 * Weichzeichner, und der Knopf tut beim zweiten Druck etwas anderes als beim
 * ersten. Wer zwei Vorlagen übereinanderlegen will, will in Wahrheit eine
 * dritte.
 */
export function clipPatch(vorlage) {
    return { effects: [...vorlage.effects], tonEffekte: [...vorlage.tonEffekte] };
}
/** Die Änderung an den Einstellungen, die diese Vorlage bedeutet. */
export function settingsPatch(vorlage) {
    return {
        width: vorlage.width,
        height: vorlage.height,
        fps: vorlage.fps,
        zielLautheit: vorlage.zielLautheit,
    };
}
/**
 * Was eine Vorlage in einem Satz sagt -- für die Liste, in der man sie
 * auswählt. „3 Filter“ hilft niemandem beim Erkennen; „Look, Farbkorrektur,
 * Kompressor“ schon.
 */
const EFFEKT_NAMEN = {
    colorAdjust: 'Farbkorrektur',
    lut: 'Look',
    blur: 'Weichzeichnen',
    vignette: 'Vignette',
    sharpen: 'Schärfen',
    chroma: 'Greenscreen',
    stabi: 'Stabilisierung',
    farbraeder: 'Farbräder',
    entrauschen: 'Rauschminderung',
    kompressor: 'Kompressor',
    equalizer: 'Equalizer',
};
export function vorlageSatz(daten) {
    if (daten.art === 'ki') {
        const wie = daten.modus === 'herausnehmen' ? 'Nur herausnehmen' : 'Auswählen und ordnen';
        return daten.zielSekunden ? `${wie} · etwa ${Math.round(daten.zielSekunden)} s` : wie;
    }
    if (daten.art === 'export') {
        const laut = daten.zielLautheit === null ? 'Lautheit so lassen' : `${daten.zielLautheit} LUFS`;
        return `${daten.width}×${daten.height} · ${daten.fps} fps · ${laut}`;
    }
    const teile = [...daten.effects, ...daten.tonEffekte].map((e) => EFFEKT_NAMEN[e.type] ?? e.type);
    return teile.length > 0 ? teile.join(', ') : 'Nichts eingestellt';
}
/**
 * Ob eine Filter-Vorlage überhaupt etwas bewirkt.
 *
 * Eine leere zu speichern ist erlaubt und sogar nützlich -- „Alles zurück“ ist
 * genau das. Aber sie soll im Blatt so heissen und nicht so aussehen, als sei
 * beim Speichern etwas schiefgegangen.
 */
export function vorlageLeer(daten) {
    return daten.art === 'filter' && daten.effects.length === 0 && daten.tonEffekte.length === 0;
}
//# sourceMappingURL=vorlagen.js.map